import { Grid, Card, CardHeader, CardContent, Box, Button } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useNotify, useRedirect, useTranslate } from 'react-admin';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';
import LanguageSwitcher from '@/components/global/menu/LanguageSwitcher.jsx';
import { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import MaasloopIcon from '@/assets/icons/maasloop.svg?react';
import RevloopIcon from '@/assets/icons/revloop.svg?react';

const StyledCard = styled(Card)(() => ({
    width: 400,
    borderRadius: '32px',
    padding: '48px 40px 16px',
    boxShadow: Palette.boxShadow.elevation3,
}));

const StyledCardHeader = styled(CardHeader)(() => ({
    padding: '0px',
    marginBottom: '14px',
    '& .MuiTypography-root': {
        fontSize: '18px',
    },
}));

const StyledCardContent = styled(CardContent)(() => ({
    padding: 0,
    '& .MuiGrid-root.MuiGrid-item': {
        width: '100%',
    },
    '& .go-to-login-button': {
        padding: '14px 16px',
        marginTop: '26px',
        '&:hover': {
            color: 'inherit',
        },
    },
}));

const apiUrl = import.meta.env.VITE_API_URL;

const ChangeEmailPage = () => {
    const { token } = useParams();
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const regex = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
        if (!regex.test(token)) {
            redirect('/login');
            return;
        }

        const run = async () => {
            try {
                setLoading(true);
                const request = new Request(`${apiUrl}/user/change-email/${token}`, {
                    method: 'POST',
                });
                const response = await fetch(request);
                if (response.ok) {
                    setSuccess(true);
                    return;
                }
                const responseBody = await response.json().catch(() => null);
                const message = responseBody?.errors?.[0]?.message || responseBody?.message || translate('account.changeEmail.notifications.error');
                notify(message, { type: 'error' });
            } catch (e) {
                const message = e?.message || translate('account.changeEmail.notifications.error');
                notify(message, { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [notify, redirect, token, translate]);

    let title;
    if (loading) {
        title = translate('account.changeEmail.title.loading');
    } else if (success) {
        title = translate('account.changeEmail.title.success');
    } else {
        title = translate('account.changeEmail.title.error');
    }

    return (
        <>
            <Box sx={{ position: 'fixed', top: 32, right: 32 }}>
                <LanguageSwitcher />
            </Box>
            <Box height={'98vh'} display='flex' alignItems='center' justifyContent='center' gap={4} p={2}>
                <StyledCard>
                    <Box sx={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                        <RevloopIcon />
                        <MaasloopIcon />
                    </Box>
                    <StyledCardHeader title={title} />
                    <StyledCardContent>
                        <Grid container>
                            <Grid item sm={12}>
                                {loading ? (
                                    <Box display='flex' justifyContent='center' mt={2}>
                                        <CircularProgress size={20} />
                                    </Box>
                                ) : (
                                    <Button
                                        className='go-to-login-button'
                                        to='/login'
                                        component={Link}
                                        color='primary'
                                        size='small'
                                        variant='contained'
                                        fullWidth
                                    >
                                        {translate('auth.backToLogin')}
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </StyledCardContent>
                </StyledCard>
            </Box>
        </>
    );
};

export default ChangeEmailPage;
